import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Trash } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { OnboardingAction, OnboardingState } from '../onboarding.types';
import { ISchoolTerm, SchoolTermEnum } from '../../../apis/models/schoolTerms';

interface Step2Props {
  state: OnboardingState;
  dispatch: React.Dispatch<OnboardingAction>;
}

const STATUS_COLOR: Record<SchoolTermEnum, 'success' | 'info' | 'default'> = {
  [SchoolTermEnum.CURRENT]: 'success',
  [SchoolTermEnum.NEXT]: 'info',
  [SchoolTermEnum.ARCHIVED]: 'default',
};

const Step2_AcademicTerms: React.FC<Step2Props> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const [termName, setTermName] = useState('');
  const [termStatus, setTermStatus] = useState<SchoolTermEnum>(SchoolTermEnum.CURRENT);

  const handleAdd = () => {
    if (!termName.trim()) return;
    const newTerm: ISchoolTerm = { term: termName.trim(), status: termStatus };
    dispatch({ type: 'SET_TERMS', payload: [...state.terms, newTerm] });
    setTermName('');
  };

  const handleRemove = (index: number) => {
    dispatch({ type: 'SET_TERMS', payload: state.terms.filter((_, i) => i !== index) });
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('onboarding.terms_hint')}
      </Typography>

      <Grid container spacing={2} alignItems="flex-end" mb={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={t('onboarding.term_name')}
            value={termName}
            onChange={(e) => setTermName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('onboarding.term_status')}</InputLabel>
            <Select
              value={termStatus}
              label={t('onboarding.term_status')}
              onChange={(e) => setTermStatus(e.target.value as SchoolTermEnum)}
            >
              <MenuItem value={SchoolTermEnum.CURRENT}>{t('onboarding.status_current')}</MenuItem>
              <MenuItem value={SchoolTermEnum.NEXT}>{t('onboarding.status_next')}</MenuItem>
              <MenuItem value={SchoolTermEnum.ARCHIVED}>{t('onboarding.status_archived')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button variant="outlined" onClick={handleAdd} fullWidth disabled={!termName.trim()}>
            {t('onboarding.btn_add')}
          </Button>
        </Grid>
      </Grid>

      {state.terms.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {t('onboarding.no_terms')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {state.terms.map((term, i) => (
            <Box
              key={i}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Typography variant="body1">{term.term}</Typography>
                <Chip label={term.status} size="small" color={STATUS_COLOR[term.status]} />
              </Box>
              <IconButton size="small" color="error" onClick={() => handleRemove(i)}>
                <Trash size={16} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default Step2_AcademicTerms;
